import { NotificationManager } from 'react-notifications';
import axios, { AxiosError } from 'axios';
import qs from 'query-string';
import { ISelectOptions } from 'src/components/ui/Select/createSelect.types';
import { isApiErrorResponse } from 'src/helpers/errors';
import { TArchivingStatusParams, TRoutingStatusParams } from 'src/store/api/payoutRouting/types';
import { IQueryParams } from 'src/types';
import { IErrorResponse } from 'src/types/errors';

import { IMerchantAccount } from '../connectedProviders/accountsPayment/types';
import { IPaymentForm, IPaymentRoutingData, IPaymentSystemGroupForm } from '../paymentRouting/types';

export const fetchPayoutRoutingRequest = async ({ page, size, sort, ...data }: IQueryParams): Promise<IPaymentRoutingData | null> => {
  try {
    const res = await axios.post('api/v1/payout/form-views', data, { params: { page, size, sort } });
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch payout routing");
    return null;
  }
};

export const fetchPayoutRouteRequest = async (id: string): Promise<Partial<IPaymentForm> | null> => {
  try {
    const res = await axios.get(`api/v1/payout/form-views/${id}`);
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch payout route");
    return null;
  }
};

export const fetchSystemGroupsRequest = async (params: IQueryParams): Promise<IPaymentSystemGroupForm[]> => {
  try {
    const res = await axios.get('api/v1/payout/sites/system-group-forms', {
      params,
      paramsSerializer: (par) => qs.stringify(par),
    });
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch system groups");
    return [];
  }
};

export const fetchSystemGroupByIdRequest = async (id: string): Promise<IPaymentSystemGroupForm> => {
  const res = await axios.get(`api/v1/payout/system-group-forms/${id}`);
  return res.data;
};

export const createPayoutFormDirectRequest = async (data: Partial<IPaymentForm>): Promise<Partial<IPaymentForm>> => {
  try {
    const res = await axios.put(`api/v1/payout/form-views/direct`, data);
    NotificationManager.success('Direct form has been created');
    return res.data;
  } catch (error: unknown) {
    const err = error as AxiosError<IErrorResponse>;

    const isErrorWithErrArray = isApiErrorResponse<IErrorResponse>(err.response, ['message', 'code', 'errors']);

    let errText;

    if (isErrorWithErrArray) {
      errText = err.response?.data.errors.map((item) => item?.message).join('\n\r') || '';
    } else {
      errText = err.response?.data.message || '';
    }

    NotificationManager.error(errText);
    throw new Error(err.response?.statusText);
  }
};

export const updatePayoutFormDirectRequest = async (data: Partial<IPaymentForm>): Promise<Partial<IPaymentForm>> => {
  try {
    const payload = { ...data };
    delete payload.id;
    delete payload.identity;
    const res = await axios.post(`api/v1/payout/form-views/direct/${data.id}`, payload);
    NotificationManager.success('Direct form has been updated');
    return res.data;
  } catch (error) {
    const err = error as AxiosError<IErrorResponse>;

    const isErrorWithErrArray = isApiErrorResponse<IErrorResponse>(err.response, ['message', 'code', 'errors']);

    let errText;

    if (isErrorWithErrArray && err.response) {
      errText = err.response.data.errors.map((item) => item?.message).join('\n\r') || '';
    } else {
      errText = err.response?.data.message || '';
    }

    NotificationManager.error(errText);

    throw new Error(err.response?.statusText);
  }
};

export const createPayoutFormRoutingRequest = async (data: Partial<IPaymentForm>): Promise<Partial<IPaymentForm>> => {
  try {
    const res = await axios.put(`api/v1/payout/form-views/routing`, data);
    NotificationManager.success('Routing form has been created');

    return res.data;
  } catch (error) {
    const err = error as AxiosError<IErrorResponse>;

    const isErrorWithErrArray = isApiErrorResponse<IErrorResponse>(err.response, ['message', 'code', 'errors']);

    let errText;

    if (isErrorWithErrArray && err.response) {
      errText = err.response.data.errors.map((item) => item?.message).join('\n\r') || '';
    } else {
      errText = err.response?.data.message || '';
    }

    NotificationManager.error(errText);
    throw new Error(err.response?.statusText);
  }
};

export const updatePayoutFormRoutingRequest = async (data: Partial<IPaymentForm>): Promise<Partial<IPaymentForm>> => {
  try {
    const payload = { ...data };
    delete payload.id;
    delete payload.identity;
    const res = await axios.post(`api/v1/payout/form-views/routing/${data.id}`, payload);
    NotificationManager.success('Routing form has been updated');
    return res.data;
  } catch (error) {
    const err = error as AxiosError<IErrorResponse>;

    const isErrorWithErrArray = isApiErrorResponse<IErrorResponse>(err.response, ['message', 'code', 'errors']);

    let errText;

    if (isErrorWithErrArray && err.response) {
      errText = err.response.data.errors.map((item) => item?.message).join('\n\r') || '';
    } else {
      errText = err.response?.data.message || '';
    }

    NotificationManager.error(errText);
    throw new Error(err.response?.statusText);
  }
};

export const updateImagePayoutFormRoutingRequest = async (id?: number, data?: FormData): Promise<void> => {
  return await axios.put(`api/v1/payout/form-views/${id}/logo`, data);
};

export const removeImagePayoutFormRoutingRequest = async (id: string): Promise<void> => {
  return await axios.delete(`api/v1/payout/form-views/${id}/logo`);
};

export const fetchPayoutProvidersSearchRequest = async (): Promise<ISelectOptions[] | []> => {
  try {
    const res = await axios.get(`api/v1/payout/search-filter/provider`, {
      headers: {
        'Content-Type': 'application/json',
      },
      data: {},
    });
    return res.data?.map((provider: string) => ({ value: provider, label: provider }));
  } catch (error) {
    NotificationManager.error("Can't get providers");
    return [];
  }
};

export const fetchPayoutMerchantAccountByFormRequest = async (id?: string): Promise<IMerchantAccount[]> => {
  if (!id) {
    return [];
  }

  try {
    const res = await axios.get(`api/v1/payout/system-group-forms/${id}/merchant-accounts`);
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch merchant accounts");
    return [];
  }
};

export const fetchPayoutMerchantAccountByIdRequest = async (id?: string): Promise<IMerchantAccount> => {
  const res = await axios.get(`api/v1/payout/merchant-accounts/${id}`);
  return res.data;
};

export const togglePayoutRoutingStatusRequest = async ({ id, enabled }: TRoutingStatusParams): Promise<void> => {
  await axios.patch(`api/v1/payout/form-views/${id}/enabled/toggle`, { enabled });
};

export const togglePayoutRoutingArchivingStatus = async ({ id, archived }: TArchivingStatusParams): Promise<void> => {
  await axios.patch(`api/v1/payout/form-views/${id}/archived`, { archived });
};
